import { type FC, useEffect } from 'react';
import styles from '../styles/shop-selection.module.scss';
import type { RepairBookingSession, Shop } from '../models/repair-bookings';
import BlueCard from '../components/blue-card';
import ActionButton from '../components/action-button';
import Select from '../components/select';
import PageActions from '../components/page-actions';
import ShopCard from '../components/shop-card';
import useShops from '../hooks/use-shops';

type ShopSelectionProps = {
    session: RepairBookingSession;
    productId?: number;
    selectedShop: Shop | null;
    onComplete: (selectedShop: Shop) => void;
    onCancel: () => void;
};

const ShopSelection: FC<ShopSelectionProps> = (props) => {
    const {
        shopsByPrefectures,
        selectedShop,
        selectedPrefecture,
        error,
        setSelectedShop,
        setSelectedPrefecture,
    } = useShops(props.session, props.productId);

    useEffect(() => {
        if (error) {
            alert(error.message);
        }
    }, [error]);

    const onComplete = (shop: Shop | null) => {
        if (shop) {
            props.onComplete(shop);
        }
    };

    return (
        <div className={styles.shopSelection}>
            {props.session.type === 'edit' && props.selectedShop && (
                <>
                    <BlueCard className={styles.selectedShop}>
                        <ShopCard
                            className={styles.shopContents}
                            header="＜予約店舗情報＞"
                            name={props.selectedShop.name}
                            address={`${props.selectedShop.region}${props.selectedShop.address}`}
                            mapUrl={props.selectedShop.map_url}
                            phone={props.selectedShop.phone_number}
                        />
                    </BlueCard>
                    <ActionButton
                        className={styles.skipShopChangeButton}
                        title="店舗変更せずに次へ"
                        onClick={() => onComplete(props.selectedShop)}
                    />
                </>
            )}
            <div className={styles.selectTitle}>
                都道府県を選択後
                <br />
                店舗を選択してください
            </div>
            <Select
                name="prefecture"
                title="都道府県を選択してください"
                options={Object.keys(shopsByPrefectures).map((s) => ({
                    value: s,
                    label: s,
                }))}
                selectedOption={selectedPrefecture}
                onSelect={setSelectedPrefecture}
            />
            {selectedPrefecture && (
                <div className={styles.editShopSelect}>
                    <div className={styles.shopList}>
                        {(shopsByPrefectures[selectedPrefecture] ?? []).map((shop) => (
                            <label key={shop.name} className={styles.shopOptionRow}>
                                <input
                                    className={styles.shopOptionCheckbox}
                                    type="checkbox"
                                    checked={selectedShop === shop}
                                    onChange={() =>
                                        setSelectedShop(selectedShop === shop ? null : shop)
                                    }
                                />
                                <ShopCard
                                    className={styles.optionCard}
                                    name={shop.name}
                                    address={`${shop.region}${shop.address}`}
                                    mapUrl={shop.map_url}
                                    phone={shop.phone_number}
                                />
                            </label>
                        ))}
                    </div>
                </div>
            )}
            <PageActions
                actionTitle="日時を選択する"
                actionButtonDisabled={selectedShop === null}
                onOk={() => onComplete(selectedShop)}
                onCancel={props.onCancel}
            />
        </div>
    );
};

export default ShopSelection;
